import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tab, TabList } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SEO } from 'src/common/components';
import { StyledTabs, StyledProfilePageContainer, StyledTabIconAndText } from 'src/features/public-profile/public-profile.styles';
import { useGetUserQuery, useTrackAnonymousViewPageMutation, useTrackViewPageMutation } from 'src/common/slices';
import { anonymousId } from 'src/common/tracking/data';
import { analytics } from 'src/common/hooks/use-segment-analytics';
import { PublicProfileTab, PublicProfileTabPanelValue } from './public-profile.types';
import { useGetPublicProfileGeneralDataQuery } from './public-profile.slice';
import { PublicProfileTabPanel, PublicProfileHeader } from './components';
import { usePublicProfileTabs } from './hooks';
export const PublicProfile = () => {
  const {
    username
  } = useParams();
  const {
    data: loggedInUser
  } = useGetUserQuery();
  const {
    data,
    isError,
    error
  } = useGetPublicProfileGeneralDataQuery({
    username: username ?? ''
  }, {
    skip: !username
  });
  const [trackAnonymousViewPage] = useTrackAnonymousViewPageMutation();
  const [trackViewPage] = useTrackViewPageMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const publicProfileData = useMemo(() => data?.data, [data]);
  const loggedInUserId = useMemo(() => loggedInUser?.data?.user?._id, [loggedInUser]);
  const publicProfileUserId = publicProfileData?._id;
  const publicProfileTabs = usePublicProfileTabs({
    loggedInUserId,
    publicProfileUserId
  });
  useEffect(() => {
    if (isError && 'status' in error && error?.status === 400) {
      navigate('/404');
    }
  }, [navigate, error, isError]);
  useEffect(() => {
    const pageName = 'public-profile';
    const trackPageLoaded = async () => {
      await (!loggedInUser || !loggedInUser.data || !loggedInUser.data.user._id ? trackAnonymousViewPage({
        anonymousId,
        name: pageName
      }) : trackViewPage({
        name: pageName
      }));
    };
    trackPageLoaded().catch(() => null);
  }, [trackAnonymousViewPage, trackViewPage, loggedInUser]);
  const handleTabChange = (value: string) => {
    const fullUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    if (value === PublicProfileTabPanelValue.SKILLS_MATRIX) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      analytics?.page({
        referrer: `${location.pathname}/skills-matrix`,
        url: fullUrl,
        path: location.pathname,
        title: 'Profile Skills Matrix',
        search: location.search
      });
    }
  };
  return <section data-sentry-component="PublicProfile" data-sentry-source-file="public-profile.tsx">
      <SEO title={publicProfileData?.username ?? ''} description="Public profile" data-sentry-element="SEO" data-sentry-source-file="public-profile.tsx" />
      <PublicProfileHeader publicProfileData={publicProfileData} isLoggedInUsersProfile={!!loggedInUserId && publicProfileData?._id === loggedInUserId} data-sentry-element="PublicProfileHeader" data-sentry-source-file="public-profile.tsx" />
      <StyledProfilePageContainer data-sentry-element="StyledProfilePageContainer" data-sentry-source-file="public-profile.tsx">
        <StyledTabs defaultValue={PublicProfileTabPanelValue.COMPLETED_ROOMS} onValueChange={handleTabChange} data-sentry-element="StyledTabs" data-sentry-source-file="public-profile.tsx">
          <TabList alignment="left" data-sentry-element="TabList" data-sentry-source-file="public-profile.tsx">
            {publicProfileTabs.map((tab: PublicProfileTab) => <Tab key={tab.value} value={tab.value}>
                <StyledTabIconAndText>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                  <FontAwesomeIcon icon={tab.icon} />
                  {tab.text}
                </StyledTabIconAndText>
              </Tab>)}
          </TabList>
          {publicProfileTabs.map((tab: PublicProfileTab) => <PublicProfileTabPanel key={tab.value} value={tab.value} userId={(publicProfileData?._id as string) ?? ''} />)}
        </StyledTabs>
      </StyledProfilePageContainer>
    </section>;
};